import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminGetVisitors, adminGetVisitorPageviews,
  adminGetKnownVisitors, adminGetVisitorChart, adminGetVisitorPages,
} from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmtDate(raw) {
  if (!raw) return '—';
  return new Date(raw).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtDateShort(raw) {
  if (!raw) return '—';
  return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtSec(s) {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
function fmtOrg(org) {
  if (!org) return '—';
  return org.replace(/^AS\d+\s+/, '');
}

function Badge({ label, color, bg }) {
  return (
    <span style={{ background: bg, color, borderRadius: 5, fontSize: 10, fontWeight: 700,
      padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function StatCard({ label, value, color, onClick, active }) {
  return (
    <div onClick={onClick} style={{ background: '#fff', border: `1px solid ${active ? color : '#e2e8f0'}`,
      borderRadius: 12, borderTop: `4px solid ${color}`, padding: '18px 22px',
      cursor: onClick ? 'pointer' : 'default',
      boxShadow: active ? `0 0 0 2px ${color}22` : 'none',
      transition: 'all 0.15s' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color, letterSpacing: '-0.5px' }}>{value ?? '—'}</div>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginTop: 3 }}>{label}</div>
    </div>
  );
}

// Simple SVG bar chart
function BarChart({ data, metric, color }) {
  if (!data || data.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No data for this period</div>
  );
  const values = data.map(d => d[metric] || 0);
  const maxVal = Math.max(...values, 1);
  const W = 600, H = 140, PAD = 40, BAR_GAP = 4;
  const slotW = (W - PAD * 2) / Math.max(data.length, 1);
  const barW = Math.min(40, Math.max(4, slotW - BAR_GAP));

  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} style={{ width: '100%', maxHeight: 180 }}>
      {/* Y gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map(frac => {
        const y = PAD + (1 - frac) * H - PAD;
        return <line key={frac} x1={PAD} x2={W - PAD} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />;
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const val = d[metric] || 0;
        const bh = (val / maxVal) * (H - PAD);
        const x = PAD + i * ((W - PAD * 2) / data.length) + BAR_GAP / 2;
        const y = H - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={color} rx={2} opacity={0.85} />
            {bh > 14 && <text x={x + barW / 2} y={y + 11} textAnchor="middle" fontSize={9} fill="#fff" fontWeight={600}>{val}</text>}
          </g>
        );
      })}
      {/* X labels — show every Nth */}
      {data.map((d, i) => {
        const step = Math.ceil(data.length / 10);
        if (i % step !== 0) return null;
        const x = PAD + i * ((W - PAD * 2) / data.length) + barW / 2 + BAR_GAP / 2;
        const label = d.period.length === 7 ? d.period : d.period.slice(5); // MM or MM-DD
        return <text key={i} x={x} y={H + 18} textAnchor="middle" fontSize={9} fill="#94a3b8">{label}</text>;
      })}
      {/* Max label */}
      <text x={PAD - 4} y={PAD - 2} textAnchor="end" fontSize={9} fill="#94a3b8">{maxVal}</text>
    </svg>
  );
}

export default function Visitors() {
  const navigate = useNavigate();
  const [sessions, setSessions]       = useState([]);
  const [expanded, setExpanded]       = useState(null);
  const [pageviews, setPageviews]     = useState({});
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [range, setRange]             = useState('7D');
  const [filter, setFilter]           = useState('all');
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [customMode, setCustomMode]   = useState(false);

  // Known contacts panel
  const [knownContacts, setKnownContacts]     = useState([]);
  const [knownLoading, setKnownLoading]       = useState(false);
  const [showKnown, setShowKnown]             = useState(false);

  // Chart
  const [chartData, setChartData]     = useState([]);
  const [chartMetric, setChartMetric] = useState('sessions');
  const [chartGran, setChartGran]     = useState('day');

  // Pages table
  const [pages, setPages]             = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);

  const RANGES = ['Today', '7D', '30D', '90D', 'All'];

  const effectiveFrom = customMode ? fromDate : '';
  const effectiveTo   = customMode ? toDate   : '';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await adminGetVisitors(range, filter, effectiveFrom, effectiveTo);
      setSessions(data.sessions || []);
      setStats(data.stats || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [range, filter, effectiveFrom, effectiveTo]);

  const loadChart = useCallback(async () => {
    try {
      const data = await adminGetVisitorChart(range, chartGran, effectiveFrom, effectiveTo);
      setChartData(data.chart || []);
    } catch (_) {}
  }, [range, chartGran, effectiveFrom, effectiveTo]);

  const loadPages = useCallback(async () => {
    setPagesLoading(true);
    try {
      const data = await adminGetVisitorPages(range, effectiveFrom, effectiveTo);
      setPages(data.pages || []);
    } catch (_) {}
    finally { setPagesLoading(false); }
  }, [range, effectiveFrom, effectiveTo]);

  useEffect(() => { load(); loadChart(); loadPages(); }, [load, loadChart, loadPages]);

  async function loadKnown() {
    setKnownLoading(true);
    try {
      const data = await adminGetKnownVisitors(range, effectiveFrom, effectiveTo);
      setKnownContacts(data.contacts || []);
    } catch (_) {}
    finally { setKnownLoading(false); }
  }

  function handleKnownClick() {
    const next = !showKnown;
    setShowKnown(next);
    if (next && knownContacts.length === 0) loadKnown();
  }

  async function togglePageviews(sid) {
    if (expanded === sid) { setExpanded(null); return; }
    setExpanded(sid);
    if (!pageviews[sid]) {
      try {
        const data = await adminGetVisitorPageviews(sid);
        setPageviews(p => ({ ...p, [sid]: data.pageviews || [] }));
      } catch (_) {}
    }
  }

  function applyCustom() { setCustomMode(true); }

  const CHART_METRICS = [
    { key: 'sessions', label: 'Sessions', color: NAVY },
    { key: 'unique_visitors', label: 'Unique Visitors', color: BLUE },
    { key: 'known_visitors', label: 'Known Visitors', color: ORANGE },
    { key: 'pageviews', label: 'Page Views', color: '#7c3aed' },
  ];
  const currentMetric = CHART_METRICS.find(m => m.key === chartMetric);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="visitors" />
      <main style={{ flex: 1, padding: '32px 36px', overflow: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: NAVY }}>Website Visitors</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Sessions tracked on masterfileai.com</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Preset range buttons */}
            <div style={{ display: 'flex', gap: 6 }}>
              {RANGES.map(r => (
                <button key={r} onClick={() => { setRange(r); setCustomMode(false); }} style={{
                  padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: range === r && !customMode ? NAVY : '#fff',
                  color: range === r && !customMode ? '#fff' : '#64748b',
                  border: `1px solid ${range === r && !customMode ? NAVY : '#e2e8f0'}`,
                }}>{r}</button>
              ))}
            </div>
            {/* Custom date range */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }} />
              <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }} />
              <button onClick={applyCustom} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: customMode ? ORANGE : '#fff',
                color: customMode ? '#fff' : '#64748b',
                border: `1px solid ${customMode ? ORANGE : '#e2e8f0'}`,
              }}>Apply</button>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 28 }}>
            <StatCard label="Total Sessions"   value={stats.total_sessions}   color={NAVY} />
            <StatCard label="Unique Visitors"  value={stats.unique_ips}       color={BLUE} />
            <StatCard label="Known Visitors"   value={stats.known_visitors}   color={ORANGE}
              onClick={handleKnownClick} active={showKnown} />
            <StatCard label="Return Visitors"  value={stats.return_visitors}  color="#059669" />
            <StatCard label="Total Page Views" value={stats.total_pageviews}  color="#7c3aed" />
          </div>
        )}

        {/* Known Contacts Panel */}
        {showKnown && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: NAVY, fontSize: 14 }}>Known Contacts — Website Activity</span>
              <button onClick={() => setShowKnown(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            {knownLoading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Contact', 'Email', 'Sessions', 'Pages Viewed', 'First Visit', 'Last Visit'].map(h => (
                      <th key={h} style={knownTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {knownContacts.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No known contacts in this period</td></tr>
                  ) : knownContacts.map(c => (
                    <tr key={c.contact_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <button onClick={() => navigate(`/admin/b2b/contacts/${c.contact_id}`)}
                          style={{ background: 'none', border: 'none', padding: 0, color: BLUE,
                            fontWeight: 600, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                          {c.first_name} {c.last_name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 16px', color: '#475569' }}>{c.email || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#475569', textAlign: 'center' }}>{c.session_count}</td>
                      <td style={{ padding: '10px 16px', color: '#475569', textAlign: 'center' }}>{c.total_pages}</td>
                      <td style={{ padding: '10px 16px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtDateShort(c.first_visit)}</td>
                      <td style={{ padding: '10px 16px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtDateShort(c.last_visit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Chart */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {CHART_METRICS.map(m => (
                <button key={m.key} onClick={() => setChartMetric(m.key)} style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: chartMetric === m.key ? m.color : '#f8fafc',
                  color: chartMetric === m.key ? '#fff' : '#64748b',
                  border: `1px solid ${chartMetric === m.key ? m.color : '#e2e8f0'}`,
                }}>{m.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['day', 'month'].map(g => (
                <button key={g} onClick={() => setChartGran(g)} style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: chartGran === g ? NAVY : '#f8fafc',
                  color: chartGran === g ? '#fff' : '#64748b',
                  border: `1px solid ${chartGran === g ? NAVY : '#e2e8f0'}`,
                  textTransform: 'capitalize',
                }}>{g}</button>
              ))}
            </div>
          </div>
          <BarChart data={chartData} metric={chartMetric} color={currentMetric?.color || NAVY} />
        </div>

        {/* Filter pills + sessions table */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['all','All Sessions'], ['known','Known Contacts'], ['new','First Visit'], ['return','Return Visit']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: filter === k ? ORANGE : '#fff',
              color: filter === k ? '#fff' : '#64748b',
              border: `1px solid ${filter === k ? ORANGE : '#e2e8f0'}`,
            }}>{l}</button>
          ))}
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 8, color: '#dc2626', padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>{error}</div>}

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Visitor / Company', 'Location', 'Landing Page', 'Referrer', 'Pages', 'First Seen', 'Flags'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', color: '#64748b',
                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                    whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              ) : sessions.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No sessions found</td></tr>
              ) : sessions.map(s => (
                <>
                  <tr key={s.session_id}
                    onClick={() => togglePageviews(s.session_id)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      background: expanded === s.session_id ? '#fafbff' : '#fff',
                      transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = expanded === s.session_id ? '#fafbff' : '#fff'}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, color: NAVY }}>
                        {s.contact_name || fmtOrg(s.org) || s.ip_address || '—'}
                      </div>
                      {s.contact_name && s.org && (
                        <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{fmtOrg(s.org)}</div>
                      )}
                      {s.contact_email && (
                        <div style={{ color: '#64748b', fontSize: 11, marginTop: 1 }}>✉ {s.contact_email}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>
                      {[s.city, s.region, s.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569', maxWidth: 160,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.landing_page || '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 12,
                      maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.referrer ? (() => { try { return new URL(s.referrer).hostname.replace('www.',''); } catch { return s.referrer; } })() : 'Direct'}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569', textAlign: 'center' }}>{s.page_count ?? 1}</td>
                    <td style={{ padding: '12px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtDate(s.first_seen_at)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {s.is_return ? <Badge label="Return" color="#059669" bg="rgba(5,150,105,0.08)" />
                                     : <Badge label="New" color={BLUE} bg="rgba(1,118,206,0.08)" />}
                        {s.contact_name && <Badge label="Known" color={ORANGE} bg="rgba(243,116,40,0.08)" />}
                      </div>
                    </td>
                  </tr>

                  {expanded === s.session_id && (
                    <tr key={`${s.session_id}-pv`}>
                      <td colSpan={7} style={{ background: '#f8fafc', padding: '0 0 0 32px',
                        borderBottom: '2px solid #e2e8f0' }}>
                        {!pageviews[s.session_id] ? (
                          <div style={{ padding: '16px', color: '#94a3b8', fontSize: 12 }}>Loading pages…</div>
                        ) : pageviews[s.session_id].length === 0 ? (
                          <div style={{ padding: '16px', color: '#94a3b8', fontSize: 12 }}>No page data</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={pvTh}>#</th>
                                <th style={pvTh}>Page</th>
                                <th style={pvTh}>Title</th>
                                <th style={pvTh}>Time on Page</th>
                                <th style={pvTh}>Viewed At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pageviews[s.session_id].map((pv, i) => (
                                <tr key={pv.pageview_id} style={{ borderTop: '1px solid #e9edf2' }}>
                                  <td style={pvTd}>{i + 1}</td>
                                  <td style={{ ...pvTd, color: BLUE, fontWeight: 500 }}>{pv.page_url}</td>
                                  <td style={{ ...pvTd, color: '#64748b' }}>{pv.page_title || '—'}</td>
                                  <td style={pvTd}>{fmtSec(pv.time_on_page)}</td>
                                  <td style={{ ...pvTd, whiteSpace: 'nowrap' }}>{fmtDate(pv.viewed_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pages table */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontWeight: 700, color: NAVY, fontSize: 14 }}>Page Performance</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Page', 'Views', 'Unique Sessions', 'Known Visitors', 'Entry', 'Exit'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Page' ? 'left' : 'center',
                    color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagesLoading ? (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              ) : pages.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No page data</td></tr>
              ) : pages.map((p, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px', color: NAVY, fontWeight: 500, maxWidth: 300,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.page_url}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>{p.view_count}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#475569' }}>{p.unique_sessions}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: p.known_visitors > 0 ? ORANGE : '#94a3b8', fontWeight: p.known_visitors > 0 ? 600 : 400 }}>{p.known_visitors}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#059669' }}>{p.entry_count}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#ef4444' }}>{p.exit_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </main>
    </div>
  );
}

const pvTh = { padding: '8px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600,
  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' };
const pvTd = { padding: '9px 14px', color: '#475569' };
const knownTh = { padding: '10px 16px', textAlign: 'left', color: '#64748b', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
  borderBottom: '1px solid #f1f5f9' };
